import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, map, Observable, of, take, tap } from 'rxjs';
import { API_URL, ENTITY_NAME, EXTERNAL } from '@core/constants';
import { IReferencesByEntity, IReferencesByProduct, ReferenceType } from '@core/configuration';
import { ApplicationStateService, AuthRequestInitialService } from '@core/service';
import { groupBy } from '@shared/utility';

@Injectable({
  providedIn: 'root',
})
export class ReferencesService {
  entityReferences$ = new BehaviorSubject<Partial<Record<ReferenceType, IReferencesByEntity[]>>>({});

  private urgency$ = new BehaviorSubject<IReferencesByEntity[]>([]);
  private referralSource$ = new BehaviorSubject<IReferencesByEntity[]>([]);
  private requestSource$ = new BehaviorSubject<IReferencesByEntity[]>([]);
  private productTypes$ = new BehaviorSubject<IReferencesByEntity[]>([]);
  private authorizationServiceLocation$ = new BehaviorSubject<IReferencesByProduct[]>([]);
  private authorizationByProduct$ = new BehaviorSubject<IReferencesByProduct[]>([]);

  urgencyOptions$: Observable<IReferencesByEntity[]> = this.urgency$;

  referralSourcesOptions$: Observable<IReferencesByEntity[]> = this.referralSource$;

  requestSourceOptions$: Observable<IReferencesByEntity[]> = this.requestSource$;

  addressLocationTypeOptions$: Observable<IReferencesByProduct[]> = this.authorizationServiceLocation$
    .pipe(
      map((refs) => refs.filter((ref) => ref.reference === ReferenceType.AddressLocationType))
    );

  addressTypeOptions$: Observable<IReferencesByProduct[]> = this.authorizationServiceLocation$
    .pipe(
      map((refs) => refs.filter((ref) => ref.reference === ReferenceType.AddressType))
    );

  staffingStatusOptions$: Observable<IReferencesByProduct[]> = this.authorizationByProduct$
    .pipe(
      map((refs) => refs.filter((ref) => ref.reference === ReferenceType.StaffingStatus))
    );

  productTypeDMEPOS$: Observable<IReferencesByEntity> = this.productTypes$
    .pipe(
      map((refs) => refs.find(ref => ref.applicationKey === 'DMEPOS'))
    );

  constructor(
    private http: HttpClient,
    private applicationStateService: ApplicationStateService,
    private authRequestInitialService: AuthRequestInitialService,
    @Inject(API_URL) private apiUrl: string
  ) {
  }

  getReferencesByProduct(entityName: string, lineOfBusinessId? :string): void {
    this.http.get<IReferencesByProduct[]>(
      this.getUrl(this.applicationStateService.tenantId, 'authorizationReferencesByProduct', entityName, lineOfBusinessId)
    )
      .pipe(take(1))
      .subscribe((references) => {
        switch (entityName) {
          case ENTITY_NAME.AuthorizationServiceLocation:
            this.authorizationServiceLocation$.next(references);
            break;

          case ENTITY_NAME.AuthorizationByProduct:
            this.authorizationByProduct$.next(references);
            break;
        }
      });
  }

  getReferencesByEntity(tenantId: string, entityName: string): Observable<IReferencesByEntity[]> {
    return this.http.get<IReferencesByEntity[]>(
      this.getUrl(tenantId, 'referencesByEntity', entityName)
    )
  }

  getReferences(entity: ReferenceType, reference?: string): Observable<IReferencesByEntity[]> {
    const entityReferences = this.entityReferences$.value;

    if (entityReferences[entity]) {
      return reference ?
        of(entityReferences[entity][reference]) :
        of(entityReferences[entity]);
    }

    return this.getReferencesByEntity(this.applicationStateService.tenantId, entity)
      .pipe(
        map((e) => groupBy(e, 'reference')),
        tap((e) => this.entityReferences$.next({
          ...entityReferences,
          [entity]: e
        })),
        map(() => reference ?
          this.entityReferences$.value[entity][reference] :
          this.entityReferences$.value[entity])
      )
  }

  getAuthorizationReferences() {
    return this.getReferencesByEntity(this.applicationStateService.tenantId, ENTITY_NAME.Authorization)
      .pipe(
        tap((references: IReferencesByEntity[]) => {
          const urgency: IReferencesByEntity[] = [];
          const referralSource: IReferencesByEntity[] = [];
          const requestSource: IReferencesByEntity[] = [];
          const productTypes: IReferencesByEntity[] = [];

          references.forEach((reference) => {
            if (reference.reference === ReferenceType.Urgency) {
              urgency.push(reference);
            }

            if (reference.reference === ReferenceType.ReferralSource) {
              referralSource.push(reference);
            }

            if (reference.reference === ReferenceType.RequestSource) {
              if (reference.applicationKey !== EXTERNAL) {
                requestSource.push(reference);
              } else {
                this.authRequestInitialService.setExternalRequestSource(reference);
              }
            }

            if (reference.reference === ReferenceType.Type) {
              productTypes.push(reference);
            }
          });

          this.urgency$.next(urgency);
          this.referralSource$.next(referralSource);
          this.requestSource$.next(requestSource);
          this.productTypes$.next(productTypes);
        })
      )
  }

  private getUrl(tenantId: string, path: string, entityName: string, lineOfBusinessId? :string): string {
    const lobParam = lineOfBusinessId ? `&lineOfBusinessId=${lineOfBusinessId}` : '';
    return `${this.apiUrl}/api/tenants/${tenantId}/references/${path}?entity=${entityName}${lobParam}`;
  }
}
